import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Container, Heading, Text, VStack, HStack, Flex, Badge, Spinner, Button, Icon, Input,
  Menu, MenuButton, MenuList, MenuItem, MenuGroup, MenuDivider, useToast,
} from '@chakra-ui/react';
import { GoogleMap, useJsApiLoader, Marker, DirectionsRenderer } from '@react-google-maps/api';
import { FaMapMarkedAlt, FaSyncAlt } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import { selectAll } from '../../utils/fetchAllRows';
import { logAssignmentChange } from '../../utils/logOutcome';
import { GOOGLE_MAPS_LOADER_OPTIONS } from '../../utils/googleMapsLoader';
import { optimizeRoute } from '../../utils/routeOptimizer';

const NYC_CENTER = { lat: 40.7128, lng: -74.006 };
const STALE_MS = 5 * 60 * 1000; // no GPS ping in 5 min → stale

const timeAgo = (iso) => {
  if (!iso) return '—';
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
};

const repName = (r) => r?.phlebotomist_profiles?.full_name || r?.phlebotomist_profiles?.email || 'Phlebotomist';
const repInitials = (r) =>
  repName(r).split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

export default function AdminActiveRoutes() {
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
  const toast = useToast();
  const [reps, setReps] = useState([]);
  const [phlebs, setPhlebs] = useState([]); // reassignment targets
  const [assignable, setAssignable] = useState([]); // phlebs with open draws but no active route
  const [allRoute, setAllRoute] = useState(null); // synthetic combined route across all phlebs
  const [buildingAll, setBuildingAll] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(''); // YYYY-MM-DD — show everyone scheduled that day
  const [buildingScheduled, setBuildingScheduled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  // 'active' = the phleb's published route snapshot (what they started in the
  // app; can be stale). 'assigned' = rebuilt on the fly from who's currently
  // assigned to them (submissions.phlebotomist_id), in memory, without touching
  // their published route.
  const [routeMode, setRouteMode] = useState('active');
  const [liveRoutes, setLiveRoutes] = useState({}); // phlebId -> synthesized rep, built from live assignments
  const [buildingLiveId, setBuildingLiveId] = useState(null);
  const [directions, setDirections] = useState(null);
  const [travelMode, setTravelMode] = useState('driving'); // 'driving' | 'transit'
  const [legTimes, setLegTimes] = useState([]); // { text, seconds } to reach each stop from the previous point
  const [drawMinutes, setDrawMinutes] = useState(() => {
    const v = parseInt(localStorage.getItem('admin_route_draw_minutes'), 10);
    return Number.isFinite(v) && v >= 0 ? v : 15;
  });
  const [startTime, setStartTime] = useState(() => localStorage.getItem('admin_route_start_time') || '09:00');
  // Local "what-if" — stops hidden from the timing calc (not removed from the
  // real route). Lets admin see how dropping a far stop changes the averages.
  const [hiddenStops, setHiddenStops] = useState(() => new Set());
  const [stopPositions, setStopPositions] = useState({}); // stopId → {lat,lng} from route legs
  const mapRef = useRef(null);

  // A leg longer than this (minutes) is flagged as a slow trip worth reconsidering.
  const LONG_LEG_MIN = 24;

  // The selected rep's service ZIPs (their chosen coverage area).
  const serviceZips = useMemo(() => {
    const z = selectedId
      ? reps.find((r) => r.phlebotomist_id === selectedId)?.phlebotomist_profiles?.service_zips
      : null;
    return new Set((Array.isArray(z) ? z : []).map((x) => String(x).trim()));
  }, [reps, selectedId]);

  // Pull a 5-digit ZIP out of a stop's address (or "lat,lng" → none).
  const zipOf = (s) => {
    const m = String(s?.address || (typeof s?.loc === 'string' ? s.loc : '') || '').match(/\b(\d{5})\b/);
    return m ? m[1] : null;
  };
  // 'in' | 'out' | 'unknown' relative to the rep's coverage.
  const coverageOf = (s) => {
    if (serviceZips.size === 0) return 'unknown';
    const z = zipOf(s);
    if (!z) return 'unknown';
    return serviceZips.has(z) ? 'in' : 'out';
  };
  // Parse a stop's {lat,lng} for a map marker, when available.
  const stopLatLng = (s) => {
    if (s?.loc && typeof s.loc === 'object' && s.loc.lat != null) return { lat: s.loc.lat, lng: s.loc.lng };
    const m = String(s?.loc || '').match(/^(-?\d+\.\d+),\s*(-?\d+\.\d+)$/);
    return m ? { lat: parseFloat(m[1]), lng: parseFloat(m[2]) } : null;
  };

  const fetchActive = async () => {
    const { data, error } = await supabase
      .from('phleb_live_locations')
      .select('phlebotomist_id, lat, lng, updated_at, route_active, route, route_started_at, phlebotomist_profiles(full_name, email, company_name, service_zips)')
      .eq('route_active', true)
      .order('updated_at', { ascending: false });
    if (!error) setReps(data || []);
    setLoading(false);
  };

  // Phlebs who have at least one open assigned draw — so admin can build a
  // route for them right from the list (no dropdown, no empty phlebs).
  const fetchAssignable = async () => {
    // No FK between submissions and phlebotomist_profiles, so we can't embed the
    // join — count by phleb id here and resolve names from the roster at render.
    const { data, error } = await selectAll(() => supabase
      .from('submissions')
      .select('phlebotomist_id, status, outcome_code')
      .not('phlebotomist_id', 'is', null));
    if (error) { console.warn('fetchAssignable:', error.message); return; }
    const map = {};
    (data || []).forEach((d) => {
      if (!isOpenSub(d)) return;
      const id = d.phlebotomist_id;
      map[id] = (map[id] || 0) + 1;
    });
    setAssignable(Object.entries(map).map(([id, count]) => ({ id, count })).sort((a, b) => b.count - a.count));
  };

  useEffect(() => {
    fetchActive();
    fetchAssignable();
    // Phleb roster for the "reassign to…" picker. Only those who finished
    // onboarding AND picked up supplies are eligible to take draws.
    supabase.from('phlebotomist_profiles')
      .select('id, full_name, company_name, email, company_address, onboarding_status, onboarding_completed_at, supplies_picked_up_at')
      .order('full_name', { ascending: true })
      .then(({ data }) => setPhlebs(data || []));
    const channel = supabase
      .channel('admin-active-routes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'phleb_live_locations' }, () => fetchActive())
      .subscribe();
    // Fallback poll so "x min ago" labels and stale flags stay fresh.
    const iv = setInterval(() => { fetchActive(); fetchAssignable(); }, 30000);
    return () => { supabase.removeChannel(channel); clearInterval(iv); };
  }, []);

  // Drop a stop from a rep's live route snapshot so it disappears from this view
  // immediately (the submission is the source of truth; this keeps them in sync).
  const dropStopFromSnapshot = async (rep, stopId) => {
    const stops = (rep.route?.stops || []).filter((s) => s.id !== stopId);
    if (rep.combined) {
      // Combined planning route lives only in state — no live row to update.
      setAllRoute((prev) => (prev ? { ...prev, route: { ...prev.route, stops } } : prev));
      return;
    }
    await supabase.from('phleb_live_locations')
      .update({ route: { ...(rep.route || {}), stops } })
      .eq('phlebotomist_id', rep.phlebotomist_id);
  };

  // Remove a stop from the route entirely (unassign the draw from the phleb).
  const removeStop = async (rep, stop) => {
    if (!window.confirm(`Remove ${stop.name} from this route?`)) return;
    try {
      await supabase.from('submissions').update({ phlebotomist_id: null }).eq('id', stop.id);
      logAssignmentChange({
        submissionId: stop.id,
        fromName: repName(rep),
        toName: null,
        actorKind: 'admin',
      });
      await dropStopFromSnapshot(rep, stop.id);
      toast({ title: `Removed ${stop.name} from the route`, status: 'success', duration: 2500 });
      setLiveRoutes({}); // rebuild the live view without the removed stop
      fetchActive();
    } catch (e) {
      toast({ title: 'Could not remove stop', description: e.message, status: 'error', duration: 4000 });
    }
  };

  // Reassign a stop to a different phleb (and drop it from this rep's route).
  const reassignStop = async (rep, stop, newPhlebId, newPhlebName) => {
    try {
      await supabase.from('submissions').update({ phlebotomist_id: newPhlebId }).eq('id', stop.id);
      logAssignmentChange({
        submissionId: stop.id,
        fromName: repName(rep),
        toName: newPhlebName,
        actorKind: 'admin',
      });
      await dropStopFromSnapshot(rep, stop.id);
      toast({ title: `Reassigned ${stop.name} to ${newPhlebName}`, status: 'success', duration: 2500 });
      setLiveRoutes({}); // both phlebs' live routes changed
      fetchActive();
    } catch (e) {
      toast({ title: 'Could not reassign stop', description: e.message, status: 'error', duration: 4000 });
    }
  };

  const selected = useMemo(
    () => {
      if (selectedId === '__all__') return allRoute;
      // 'assigned' mode renders the in-memory live route (built from current
      // assignments) instead of the phleb's published snapshot.
      if (routeMode === 'assigned') return liveRoutes[selectedId] || null;
      return reps.find((r) => r.phlebotomist_id === selectedId) || null;
    },
    [reps, selectedId, allRoute, routeMode, liveRoutes]
  );
  const phlebById = useMemo(() => {
    const m = {}; phlebs.forEach((p) => { m[p.id] = p; }); return m;
  }, [phlebs]);

  // In 'assigned' mode, build the selected phleb's live route on demand (fetch
  // their current open draws + optimize) and cache it. Recomputed when the phleb
  // is reselected after a Refresh (which clears the cache). Skips '__all__'.
  useEffect(() => {
    if (routeMode !== 'assigned' || !selectedId || selectedId === '__all__') return;
    if (!isLoaded || !window.google) return;
    if (liveRoutes[selectedId] || buildingLiveId === selectedId) return;
    let cancelled = false;
    (async () => {
      setBuildingLiveId(selectedId);
      try {
        const built = await computeAssignedRoute(selectedId);
        if (cancelled) return;
        const snapRep = reps.find((r) => r.phlebotomist_id === selectedId);
        const prof = phlebById[selectedId] || snapRep?.phlebotomist_profiles || {};
        setLiveRoutes((prev) => ({
          ...prev,
          [selectedId]: {
            phlebotomist_id: selectedId,
            live: true,
            lat: snapRep?.lat ?? null,
            lng: snapRep?.lng ?? null,
            updated_at: snapRep?.updated_at || null,
            phlebotomist_profiles: prof,
            route: built ? built.route : { stops: [] },
          },
        }));
      } catch (e) {
        if (!cancelled) toast({ title: 'Could not build live route', description: e.message, status: 'error', duration: 4000 });
      } finally {
        if (!cancelled) setBuildingLiveId(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeMode, selectedId, isLoaded, liveRoutes]);

  // Render the selected rep's route line through the VISIBLE stops (so hiding a
  // stop redraws the route), and capture each stop's geocoded position from the
  // result legs — that gives coords even for address-only stops, so every
  // visible stop gets a map marker.
  useEffect(() => {
    setDirections(null);
    setStopPositions({});
    if (!isLoaded || !window.google || !selected) return;
    const allStops = Array.isArray(selected.route?.stops) ? selected.route.stops : [];
    const stops = allStops.filter((s) => !hiddenStops.has(s.id));
    if (stops.length === 0) return;
    const origin = (selected.lat != null && selected.lng != null)
      ? { lat: selected.lat, lng: selected.lng }
      : (selected.route?.start || stops[0].loc);
    const destination = stops[stops.length - 1].loc;
    const middle = stops.slice(0, -1);
    const svc = new window.google.maps.DirectionsService();
    svc.route({
      origin,
      destination,
      waypoints: middle.map((s) => ({ location: s.loc, stopover: true })),
      travelMode: window.google.maps.TravelMode.DRIVING,
    }, (res, status) => {
      if (status === 'OK') setDirections(res);
    });
  }, [isLoaded, selected, hiddenStops]);

  // Resolve a map position for every VISIBLE stop: use stored lat/lng when
  // present, otherwise geocode the address. Independent of the route line, so a
  // marker shows for every stop (including address-only ones).
  useEffect(() => {
    setStopPositions({});
    if (!isLoaded || !window.google || !selected) return;
    const stops = (selected.route?.stops || []).filter((s) => !hiddenStops.has(s.id));
    if (stops.length === 0) return;
    const geocoder = new window.google.maps.Geocoder();
    let cancelled = false;
    (async () => {
      const pos = {};
      for (const s of stops) {
        const parsed = stopLatLng(s);
        if (parsed) { pos[s.id] = parsed; setStopPositions({ ...pos }); continue; }
        const addr = s.address || (typeof s.loc === 'string' ? s.loc : '');
        if (!addr) continue;
        // eslint-disable-next-line no-await-in-loop
        const loc = await new Promise((resolve) => {
          geocoder.geocode({ address: addr }, (results, status) => {
            resolve(status === 'OK' && results?.[0] ? results[0].geometry.location : null);
          });
        });
        if (cancelled) return;
        if (loc) { pos[s.id] = { lat: loc.lat(), lng: loc.lng() }; setStopPositions({ ...pos }); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, selected, hiddenStops]);

  // Reset the what-if hidden set whenever a different rep is opened.
  useEffect(() => { setHiddenStops(new Set()); }, [selectedId]);

  // Departure timestamp for traffic-aware driving ETAs, built from the route
  // date (scheduleDate) + Start time. Google only predicts traffic for a
  // departureTime that is now-or-future — it has no historical traffic — so
  // this returns null for a past/blank time and the request falls back to
  // typical (non-traffic) durations. When set, DRIVING legs come back with
  // duration_in_traffic.
  const routeDepartureDate = useMemo(() => {
    const [h, m] = (startTime || '09:00').split(':').map(Number);
    let d;
    if (scheduleDate) {
      const [y, mo, day] = scheduleDate.split('-').map(Number);
      if (!y || !mo || !day) return null;
      d = new Date(y, mo - 1, day, h || 0, m || 0, 0, 0);
    } else {
      // No date picked → assume today at the Start time.
      d = new Date();
      d.setHours(h || 0, m || 0, 0, 0);
    }
    // Google rejects a departureTime in the past; require a small future margin.
    if (d.getTime() <= Date.now() + 60000) return null;
    return d;
  }, [scheduleDate, startTime]);

  // Per-leg travel time keyed by stop id, computed over the VISIBLE stops
  // (skipping hidden ones) so the what-if recomputes when you hide a stop.
  // origin → firstVisible → … in the chosen mode (transit = leave now,
  // driving = traffic at routeDepartureDate when that's a future time).
  useEffect(() => {
    setLegTimes({});
    if (!isLoaded || !window.google || !selected) return;
    const allStops = Array.isArray(selected.route?.stops) ? selected.route.stops : [];
    const stops = allStops.filter((s) => !hiddenStops.has(s.id));
    if (stops.length === 0) return;
    const firstOrigin = (selected.lat != null && selected.lng != null)
      ? { lat: selected.lat, lng: selected.lng }
      : (selected.route?.start || stops[0].loc);
    const svc = new window.google.maps.DirectionsService();
    let cancelled = false;
    (async () => {
      const out = {};
      for (let i = 0; i < stops.length; i++) {
        const origin = i === 0 ? firstOrigin : stops[i - 1].loc;
        const destination = stops[i].loc;
        if (!origin || !destination) { out[stops[i].id] = null; continue; }
        const req = {
          origin,
          destination,
          travelMode: travelMode === 'transit'
            ? window.google.maps.TravelMode.TRANSIT
            : window.google.maps.TravelMode.DRIVING,
        };
        if (travelMode === 'transit') {
          req.transitOptions = { departureTime: new Date() };
        } else if (routeDepartureDate) {
          // Traffic-aware ETA for the chosen departure time. All legs use the
          // route's single start time rather than each leg's cumulative arrival
          // — a simplification that slightly under-weights traffic on later
          // stops but keeps one predicted departure for the whole route.
          req.drivingOptions = {
            departureTime: routeDepartureDate,
            trafficModel: window.google.maps.TrafficModel.BEST_GUESS,
          };
        }
        // eslint-disable-next-line no-await-in-loop
        const dur = await new Promise((resolve) => {
          svc.route(req, (res, status) => {
            const leg = status === 'OK' && res?.routes?.[0]?.legs?.[0];
            // duration_in_traffic is only present when drivingOptions was sent
            // and the key is entitled for it; fall back to plain duration.
            const durObj = leg && (leg.duration_in_traffic || leg.duration);
            resolve(durObj
              ? { text: durObj.text, seconds: durObj.value, first: i === 0, traffic: !!leg.duration_in_traffic }
              : { text: travelMode === 'transit' ? 'no transit' : null, seconds: null, first: i === 0 });
          });
        });
        if (cancelled) return;
        out[stops[i].id] = dur;
        setLegTimes({ ...out });
      }
    })();
    return () => { cancelled = true; };
  }, [isLoaded, selected, travelMode, hiddenStops, routeDepartureDate]);

  const toggleHidden = (stopId) => {
    setHiddenStops((prev) => {
      const next = new Set(prev);
      next.has(stopId) ? next.delete(stopId) : next.add(stopId);
      return next;
    });
  };

  const [optimizing, setOptimizing] = useState(false);

  // Re-optimize the selected route to the fastest order in the current mode
  // (driving/transit) using the client-side optimizer, then persist the new
  // order + total back to the live snapshot.
  const reoptimize = async (rep) => {
    const stops = Array.isArray(rep.route?.stops) ? rep.route.stops : [];
    if (stops.length < 2) { toast({ title: 'Need at least 2 stops to optimize', status: 'info', duration: 2500 }); return; }
    const origin = (rep.lat != null && rep.lng != null)
      ? { lat: rep.lat, lng: rep.lng }
      : (rep.route?.start || stops[0].loc);
    setOptimizing(true);
    try {
      const { order, totalSeconds } = await optimizeRoute({
        origin,
        stops: stops.map((s) => ({ id: s.id, location: s.loc })),
        mode: travelMode,
      });
      const byId = new Map(stops.map((s) => [s.id, s]));
      const newStops = order.map((id) => byId.get(id)).filter(Boolean);
      const newRoute = { ...(rep.route || {}), stops: newStops, totalMinutes: Math.round(totalSeconds / 60) };
      if (rep.combined) {
        // Combined planning route — keep it in state only.
        setAllRoute((prev) => (prev ? { ...prev, route: newRoute } : prev));
      } else {
        await supabase.from('phleb_live_locations').update({ route: newRoute }).eq('phlebotomist_id', rep.phlebotomist_id);
        fetchActive();
      }
      toast({ title: `Re-optimized — ${Math.round(totalSeconds / 60)}m total (${travelMode})`, status: 'success', duration: 3000 });
    } catch (e) {
      toast({ title: 'Could not optimize', description: e.message, status: 'error', duration: 4000 });
    } finally {
      setOptimizing(false);
    }
  };

  // ── Build & publish a route FOR a phleb ───────────────────────────────────
  // Admin picks a phleb; we pull all their open assigned draws, optimize the
  // order, and publish to phleb_live_locations so the phleb just logs in and
  // sees a ready route.
  const [buildingForId, setBuildingForId] = useState(null);

  const fmtAddr = (d) => d.patient_address
    || [d.patient_street, d.patient_city, d.patient_state, d.patient_zip].filter(Boolean).join(', ');
  const isOpenSub = (d) => {
    const st = (d.status || '').toLowerCase();
    if (['completed', 'cancelled', 'out_of_area', 'waiting_on_lab_results', 'waiting_to_be_received'].includes(st)) return false;
    if ((d.outcome_code || '') === 'successful_draw') return false;
    return true;
  };

  // Scheduled-date matching — same local-date logic as the Blood Files filter,
  // so exact times and availability windows both resolve to the right day(s).
  const localYmd = (value) => {
    if (!value) return null;
    const raw = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const dt = new Date(raw);
    if (isNaN(dt.getTime())) return null;
    const p = (n) => String(n).padStart(2, '0');
    return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
  };
  const scheduledRange = (d) => {
    const type = d.scheduling_type || (d.availability_start_date ? 'window' : (d.blood_collection_time ? 'exact' : null));
    if ((type === 'window' || type === 'all_day') && d.availability_start_date) {
      const start = localYmd(d.availability_start_date);
      const end = localYmd(d.availability_end_date || d.availability_start_date) || start;
      if (start) return { start, end };
    }
    const day = localYmd(d.blood_collection_time);
    if (day) return { start: day, end: day };
    if (d.availability_start_date) {
      const start = localYmd(d.availability_start_date);
      const end = localYmd(d.availability_end_date || d.availability_start_date) || start;
      if (start) return { start, end };
    }
    return null;
  };
  const scheduledOnDate = (d, ymd) => {
    const r = scheduledRange(d);
    return !!r && r.start <= ymd && ymd <= r.end;
  };

  // Everyone scheduled on the chosen date (any phleb, plus unassigned), as one
  // combined planning route. Independent of route_active / assignment.
  const buildScheduledRoute = async () => {
    if (!scheduleDate) { toast({ title: 'Pick a date first', status: 'warning', duration: 2500 }); return; }
    if (!window.google) { toast({ title: 'Map not loaded yet — try again in a second', status: 'warning', duration: 2500 }); return; }
    setBuildingScheduled(true);
    try {
      const { data: subs, error } = await selectAll(() => supabase
        .from('submissions')
        .select('id, patient_name, patient_lat, patient_lng, patient_address, patient_street, patient_city, patient_state, patient_zip, status, outcome_code, phlebotomist_id, scheduling_type, blood_collection_time, availability_start_date, availability_end_date')
        .or('blood_collection_time.not.is.null,availability_start_date.not.is.null'));
      if (error) throw error;
      const stops = (subs || [])
        .filter((d) => (d.status || '').toLowerCase() !== 'cancelled')
        .filter((d) => scheduledOnDate(d, scheduleDate))
        .map((d) => {
          const loc = (d.patient_lat != null && d.patient_lng != null) ? `${d.patient_lat},${d.patient_lng}` : fmtAddr(d);
          return loc ? { id: d.id, name: d.patient_name || 'Patient', loc, address: fmtAddr(d), phleb: d.phlebotomist_id || null } : null;
        })
        .filter(Boolean);
      if (stops.length === 0) {
        setAllRoute(null);
        toast({ title: `No patients scheduled for ${scheduleDate}`, status: 'info', duration: 3000 });
        return;
      }
      const origin = `${NYC_CENTER.lat},${NYC_CENTER.lng}`;
      let ordered = stops;
      let totalMin = 0;
      if (stops.length > 1) {
        const { order, totalSeconds } = await optimizeRoute({ origin, stops: stops.map((s) => ({ id: s.id, location: s.loc })), mode: 'driving' });
        const byId = new Map(stops.map((s) => [s.id, s]));
        ordered = order.map((id) => byId.get(id)).filter(Boolean);
        totalMin = Math.round((totalSeconds || 0) / 60);
      }
      setAllRoute({
        phlebotomist_id: '__all__',
        combined: true,
        scheduledFor: scheduleDate,
        lat: null,
        lng: null,
        route: { start: origin, totalMinutes: totalMin, stops: ordered.map((s) => ({ id: s.id, name: s.name, loc: s.loc, address: s.address })) },
      });
      setSelectedId('__all__');
      toast({ title: `${stops.length} patient${stops.length === 1 ? '' : 's'} scheduled for ${scheduleDate}`, status: 'success', duration: 3000 });
    } catch (e) {
      toast({ title: 'Could not build scheduled route', description: e.message, status: 'error', duration: 5000 });
    } finally {
      setBuildingScheduled(false);
    }
  };

  // Build a route object from a phleb's CURRENT open assigned draws (fetch +
  // optimize). Pure compute — no DB write — so it backs both the in-memory
  // "Assigned (live)" view and the publish action below.
  // Returns { stops, route } or null when the phleb has no open assigned draws.
  const computeAssignedRoute = async (phlebId) => {
    const { data: subs, error } = await selectAll(() => supabase
      .from('submissions')
      .select('id, patient_name, patient_lat, patient_lng, patient_address, patient_street, patient_city, patient_state, patient_zip, status, outcome_code')
      .eq('phlebotomist_id', phlebId));
    if (error) throw error;
    const stops = (subs || []).filter(isOpenSub).map((d) => {
      const loc = (d.patient_lat != null && d.patient_lng != null) ? `${d.patient_lat},${d.patient_lng}` : fmtAddr(d);
      return loc ? { id: d.id, name: d.patient_name || 'Patient', loc, address: fmtAddr(d) } : null;
    }).filter(Boolean);
    if (stops.length === 0) return null;

    const snapRep = reps.find((r) => r.phlebotomist_id === phlebId);
    const phleb = phlebs.find((p) => p.id === phlebId) || phlebById[phlebId] || {};
    // Prefer the phleb's live GPS as the route origin (matches the snapshot
    // view), else their company address, else the first stop.
    const origin = (snapRep?.lat != null && snapRep?.lng != null)
      ? `${snapRep.lat},${snapRep.lng}`
      : (phleb.company_address || stops[0].loc);

    let ordered = stops;
    let totalMin = 0;
    if (stops.length > 1) {
      const { order, totalSeconds } = await optimizeRoute({ origin, stops: stops.map((s) => ({ id: s.id, location: s.loc })), mode: 'driving' });
      const byId = new Map(stops.map((s) => [s.id, s]));
      ordered = order.map((id) => byId.get(id)).filter(Boolean);
      totalMin = Math.round((totalSeconds || 0) / 60);
    }
    return {
      stops,
      route: {
        start: origin,
        totalMinutes: totalMin,
        stops: ordered.map((s) => ({ id: s.id, name: s.name, loc: s.loc, address: s.address })),
      },
    };
  };

  const buildRouteForPhleb = async (phlebId) => {
    if (!phlebId) return;
    if (!window.google) { toast({ title: 'Map not loaded yet — try again in a second', status: 'warning', duration: 2500 }); return; }
    setBuildingForId(phlebId);
    try {
      const built = await computeAssignedRoute(phlebId);
      if (!built) { toast({ title: 'No open draws assigned to this phleb', status: 'info', duration: 3000 }); return; }
      const { stops } = built;
      const phleb = phlebs.find((p) => p.id === phlebId);
      const route = {
        ...built.route,
        startedAt: new Date().toISOString(),
        builtByAdmin: true,
      };
      const totalMin = route.totalMinutes;
      const { error: upErr } = await supabase.from('phleb_live_locations').upsert(
        { phlebotomist_id: phlebId, route_active: true, route_started_at: route.startedAt, route, updated_at: new Date().toISOString() },
        { onConflict: 'phlebotomist_id' }
      );
      if (upErr) throw upErr;
      toast({ title: `Route published for ${phleb?.full_name || 'phleb'}`, description: `${stops.length} stop${stops.length === 1 ? '' : 's'} · ~${totalMin}m`, status: 'success', duration: 4000 });
      setSelectedId(phlebId);
      fetchActive();
      fetchAssignable();
    } catch (e) {
      toast({ title: 'Could not build route', description: e.message, status: 'error', duration: 5000 });
    } finally {
      setBuildingForId(null);
    }
  };

  // Build ONE combined route across every phleb's open assigned draws — a
  // planning view (not published to any phleb), shown as the "All phlebs" item.
  const buildAllRoute = async () => {
    if (!window.google) { toast({ title: 'Map not loaded yet — try again in a second', status: 'warning', duration: 2500 }); return; }
    setBuildingAll(true);
    try {
      const { data: subs, error } = await selectAll(() => supabase
        .from('submissions')
        .select('id, patient_name, patient_lat, patient_lng, patient_address, patient_street, patient_city, patient_state, patient_zip, status, outcome_code, phlebotomist_id')
        .not('phlebotomist_id', 'is', null));
      if (error) throw error;
      const stops = (subs || []).filter(isOpenSub).map((d) => {
        const loc = (d.patient_lat != null && d.patient_lng != null) ? `${d.patient_lat},${d.patient_lng}` : fmtAddr(d);
        return loc ? { id: d.id, name: d.patient_name || 'Patient', loc, address: fmtAddr(d) } : null;
      }).filter(Boolean);
      if (stops.length === 0) { toast({ title: 'No open assigned draws to combine', status: 'info', duration: 3000 }); return; }

      const origin = `${NYC_CENTER.lat},${NYC_CENTER.lng}`;
      let ordered = stops;
      let totalMin = 0;
      if (stops.length > 1) {
        const { order, totalSeconds } = await optimizeRoute({ origin, stops: stops.map((s) => ({ id: s.id, location: s.loc })), mode: 'driving' });
        const byId = new Map(stops.map((s) => [s.id, s]));
        ordered = order.map((id) => byId.get(id)).filter(Boolean);
        totalMin = Math.round((totalSeconds || 0) / 60);
      }
      setAllRoute({
        phlebotomist_id: '__all__',
        combined: true,
        lat: null,
        lng: null,
        route: { start: origin, totalMinutes: totalMin, stops: ordered.map((s) => ({ id: s.id, name: s.name, loc: s.loc, address: s.address })) },
      });
      setSelectedId('__all__');
    } catch (e) {
      toast({ title: 'Could not build combined route', description: e.message, status: 'error', duration: 5000 });
    } finally {
      setBuildingAll(false);
    }
  };

  const selectRep = (r) => {
    setSelectedId(r.phlebotomist_id);
    if (r.lat != null && r.lng != null && mapRef.current) {
      mapRef.current.panTo({ lat: r.lat, lng: r.lng });
      mapRef.current.setZoom(13);
    }
  };

  // Only phlebs who finished onboarding AND picked up supplies can take draws.
  const onboardingDone = (p) =>
    !!p.onboarding_completed_at || ['completed', 'complete', 'done'].includes((p.onboarding_status || '').toLowerCase());
  const eligiblePhlebs = phlebs.filter((p) => onboardingDone(p) && p.supplies_picked_up_at);

  const setDraw = (v) => {
    const n = Math.max(0, parseInt(v, 10) || 0);
    setDrawMinutes(n);
    try { localStorage.setItem('admin_route_draw_minutes', String(n)); } catch { /* ignore */ }
  };
  const setStart = (v) => {
    setStartTime(v);
    try { localStorage.setItem('admin_route_start_time', v); } catch { /* ignore */ }
  };
  // "HH:mm" + minutes → "h:mm AM/PM" (with +Nd if it rolls past midnight).
  const addMinutesToTime = (hhmm, addMin) => {
    const [h, m] = (hhmm || '09:00').split(':').map(Number);
    const total = (h || 0) * 60 + (m || 0) + addMin;
    const dayMin = ((total % 1440) + 1440) % 1440;
    const hh = Math.floor(dayMin / 60); const mm = dayMin % 60;
    const ampm = hh >= 12 ? 'PM' : 'AM';
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    const plusDays = Math.floor(total / 1440);
    return `${h12}:${String(mm).padStart(2, '0')} ${ampm}${plusDays > 0 ? ` (+${plusDays}d)` : ''}`;
  };

  // The selected rep's route detail — rendered inline under their card in the
  // left list. Clicks inside it shouldn't bubble up to re-select the card.
  const routeDetail = (rep) => {
    const allStops = rep.route?.stops || [];
    const visibleStops = allStops.filter((s) => !hiddenStops.has(s.id));
    const visNum = new Map(); visibleStops.forEach((s, i) => visNum.set(s.id, i + 1));
    const travelSec = visibleStops.reduce((sum, s) => sum + (legTimes[s.id]?.seconds || 0), 0);
    const nVis = visibleStops.length;
    const totalMin = Math.round(travelSec / 60) + drawMinutes * nVis;
    const avg = nVis ? Math.round(totalMin / nVis) : 0;
    const longCount = visibleStops.filter((s) => (legTimes[s.id]?.seconds || 0) / 60 > LONG_LEG_MIN).length;
    return (
    <Box mt={3} pt={3} borderTopWidth="1px" borderColor="gray.200" onClick={(e) => e.stopPropagation()}>
      <HStack spacing={2} mb={2} flexWrap="wrap">
        <Button size="xs" colorScheme="green" leftIcon={<Text as="span">⚡</Text>}
          isLoading={optimizing} loadingText="Optimizing"
          onClick={() => reoptimize(rep)}>
          Re-optimize ({travelMode === 'transit' ? 'transit' : 'drive'})
        </Button>
        <HStack spacing={0} borderWidth="1px" borderColor="gray.300" borderRadius="md" overflow="hidden">
          <Button size="xs" borderRadius={0}
            colorScheme={travelMode === 'driving' ? 'blue' : 'gray'}
            variant={travelMode === 'driving' ? 'solid' : 'ghost'}
            onClick={() => setTravelMode('driving')}>🚗</Button>
          <Button size="xs" borderRadius={0}
            colorScheme={travelMode === 'transit' ? 'blue' : 'gray'}
            variant={travelMode === 'transit' ? 'solid' : 'ghost'}
            onClick={() => setTravelMode('transit')}>🚆</Button>
        </HStack>
      </HStack>
      {/* Start time + draw time per stop — drive the schedule below. */}
      <HStack spacing={3} mb={2} flexWrap="wrap">
        <HStack spacing={1}>
          <Text fontSize="xs" color="gray.600" fontWeight="600">Start</Text>
          <Input size="xs" w="110px" type="time" value={startTime} onChange={(e) => setStart(e.target.value)} />
        </HStack>
        <HStack spacing={1}>
          <Text fontSize="xs" color="gray.600" fontWeight="600">Draw</Text>
          <Input size="xs" w="56px" type="number" min={0} value={drawMinutes} onChange={(e) => setDraw(e.target.value)} />
          <Text fontSize="xs" color="gray.500">min/stop</Text>
        </HStack>
        {travelMode !== 'transit' && (
          <Badge colorScheme={routeDepartureDate ? 'orange' : 'gray'} variant="subtle" fontSize="2xs">
            {routeDepartureDate
              ? `traffic @ ${routeDepartureDate.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
              : 'typical traffic (pick a future date/time)'}
          </Badge>
        )}
      </HStack>
      <Box bg="gray.50" borderRadius="md" p={2} mb={2}>
        <HStack justify="space-between" mb={1}>
          <Text fontSize="2xs" color="gray.500" textTransform="uppercase" fontWeight="700" letterSpacing="wide">Avg / draw</Text>
          <Text fontSize="sm" fontWeight="800" color={avg > LONG_LEG_MIN ? 'red.600' : 'blue.600'}>{avg} min</Text>
        </HStack>
        <HStack justify="space-between">
          <Text fontSize="2xs" color="gray.600">Total</Text>
          <Text fontSize="xs" fontWeight="700">{Math.floor(totalMin / 60) ? `${Math.floor(totalMin / 60)}h ` : ''}{totalMin % 60}m</Text>
        </HStack>
        <HStack justify="space-between">
          <Text fontSize="2xs" color="gray.600">Done by</Text>
          <Text fontSize="xs" fontWeight="700" color="green.600">{addMinutesToTime(startTime, totalMin)}</Text>
        </HStack>
        <Text fontSize="2xs" color="gray.400" mt={1}>
          {Math.round(travelSec / 60)}m {travelMode} + {drawMinutes * nVis}m draws · {nVis} stop{nVis === 1 ? '' : 's'}
          {hiddenStops.size ? ` · ${hiddenStops.size} hidden` : ''}
        </Text>
        {longCount > 0 && (
          <Text fontSize="2xs" color="red.600" fontWeight="700" mt={1}>
            ⚠ {longCount} slow trip{longCount === 1 ? '' : 's'} over {LONG_LEG_MIN}m — consider hiding to speed up the route
          </Text>
        )}
      </Box>
      <VStack align="stretch" spacing={0}>
        {allStops.map((s, i) => {
          const hidden = hiddenStops.has(s.id);
          const leg = legTimes[s.id];
          const legMin = leg?.seconds != null ? Math.round(leg.seconds / 60) : null;
          const slow = !hidden && legMin != null && legMin > LONG_LEG_MIN;
          return (
          <Box key={s.id || i} opacity={hidden ? 0.45 : 1}>
            {!hidden && (
              <HStack spacing={2} pl="20px" color={slow ? 'red.500' : 'gray.400'} fontSize="2xs" py={0.5}>
                <Text>{travelMode === 'transit' ? '🚆' : '🚗'}</Text>
                {(() => {
                  const travel = leg?.text || '…';
                  if (leg?.first) return <Text fontWeight={slow ? '700' : '400'}>{travel} from current location{slow ? ' ⚠ slow' : ''}</Text>;
                  const totalText = leg?.seconds != null
                    ? `${legMin + drawMinutes}m (${travel} drive + ${drawMinutes}m draw)`
                    : `${travel} + ${drawMinutes}m draw`;
                  return <Text fontWeight={slow ? '700' : '400'}>{totalText}{slow ? ' ⚠ slow' : ''}</Text>;
                })()}
              </HStack>
            )}
            <HStack spacing={2} align="start" justify="space-between">
              <HStack spacing={2} align="start" flex={1} minW={0}>
                <Text color="gray.400" w="20px" fontWeight="700" fontSize="sm">{hidden ? '–' : `${visNum.get(s.id)}.`}</Text>
                <Box minW={0}>
                  <HStack spacing={1.5}>
                    <Text fontSize="sm" fontWeight="600" textDecoration={hidden ? 'line-through' : 'none'}>{s.name}</Text>
                    {(() => {
                      const cov = coverageOf(s);
                      if (cov === 'out') return <Badge colorScheme="red" fontSize="2xs" flexShrink={0}>outside zone</Badge>;
                      if (cov === 'in') return <Badge colorScheme="green" variant="subtle" fontSize="2xs" flexShrink={0}>in zone</Badge>;
                      return null;
                    })()}
                  </HStack>
                  <Text fontSize="xs" color="gray.500">{s.address || (typeof s.loc === 'string' ? s.loc : '—')}</Text>
                </Box>
              </HStack>
              <HStack spacing={1} flexShrink={0}>
                <Button size="xs" variant="ghost" colorScheme={hidden ? 'green' : 'gray'} onClick={() => toggleHidden(s.id)}>
                  {hidden ? 'Show' : 'Hide'}
                </Button>
              <Menu isLazy>
                <MenuButton as={Button} size="xs" variant="ghost" flexShrink={0}>⋯</MenuButton>
                <MenuList maxH="320px" overflowY="auto">
                  {(() => {
                    const stops = rep.route?.stops || [];
                    const firstOrigin = (rep.lat != null && rep.lng != null)
                      ? `${rep.lat},${rep.lng}`
                      : (typeof rep.route?.start === 'string' ? rep.route.start : '');
                    const prev = i === 0 ? firstOrigin : stops[i - 1];
                    const originStr = i === 0 ? firstOrigin
                      : (prev?.address || (prev?.loc && typeof prev.loc === 'object' ? `${prev.loc.lat},${prev.loc.lng}` : prev?.loc) || '');
                    const destStr = s.address || (s.loc && typeof s.loc === 'object' ? `${s.loc.lat},${s.loc.lng}` : s.loc) || '';
                    const url = `https://www.google.com/maps/dir/?api=1${originStr ? `&origin=${encodeURIComponent(originStr)}` : ''}&destination=${encodeURIComponent(destStr)}&travelmode=${travelMode}`;
                    return (
                      <MenuItem as="a" href={url} target="_blank" rel="noopener noreferrer">
                        {travelMode === 'transit' ? '🚆' : '🚗'} Directions to this stop
                      </MenuItem>
                    );
                  })()}
                  <MenuItem color="red.600" onClick={() => removeStop(rep, s)}>Remove from route</MenuItem>
                  <MenuDivider />
                  <MenuGroup title="Reassign to (onboarded + supplies)">
                    {(() => {
                      const targets = eligiblePhlebs.filter((p) => p.id !== rep.phlebotomist_id);
                      if (targets.length === 0) {
                        return <MenuItem isDisabled>No eligible phlebs</MenuItem>;
                      }
                      return targets.map((p) => {
                        const nm = p.full_name || p.company_name || p.email || 'Phlebotomist';
                        return (
                          <MenuItem key={p.id} onClick={() => reassignStop(rep, s, p.id, nm)}>{nm}</MenuItem>
                        );
                      });
                    })()}
                  </MenuGroup>
                </MenuList>
              </Menu>
              </HStack>
            </HStack>
          </Box>
          );
        })}
        {allStops.length === 0 && (
          <Text fontSize="sm" color="gray.400">No stops recorded for this route.</Text>
        )}
      </VStack>
    </Box>
    );
  };

  return (
    <Container maxW="container.xl" py={6}>
      <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
        <HStack spacing={3}>
          <Icon as={FaMapMarkedAlt} color="blue.500" boxSize={6} />
          <Box>
            <Heading size="lg">Active Routes</Heading>
            <Text fontSize="sm" color="gray.500">
              {reps.length} rep{reps.length === 1 ? '' : 's'} on a route right now
              {(() => {
                const total = reps.reduce((sum, r) => sum + (Number(r.route?.totalMinutes) || 0), 0);
                if (!total) return '';
                const h = Math.floor(total / 60); const m = total % 60;
                return ` · ${h ? `${h}h ` : ''}${m}m total commute`;
              })()}
            </Text>
          </Box>
        </HStack>
        <HStack spacing={2} flexWrap="wrap">
          {/* Show everyone scheduled for a specific date, as a combined route. */}
          <Text fontSize="sm" color="gray.600" fontWeight="600">Scheduled for</Text>
          <Input size="sm" type="date" w="160px" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} bg="white" />
          <Button size="sm" colorScheme="purple" isLoading={buildingScheduled} loadingText="Loading"
            onClick={buildScheduledRoute} isDisabled={!scheduleDate}>
            Show scheduled
          </Button>
          {allRoute?.scheduledFor && (
            <Button size="sm" variant="ghost" onClick={() => { setAllRoute(null); setScheduleDate(''); }}>Clear</Button>
          )}
          {/* Snapshot (what the phleb started) vs live (rebuilt from current
              assignments). Switching to 'assigned' clears any built cache so it
              recomputes fresh. */}
          <HStack spacing={0} borderWidth="1px" borderColor="gray.300" borderRadius="md" overflow="hidden">
            <Button size="sm" borderRadius={0} variant={routeMode === 'active' ? 'solid' : 'ghost'}
              colorScheme={routeMode === 'active' ? 'blue' : 'gray'}
              onClick={() => setRouteMode('active')}>Active</Button>
            <Button size="sm" borderRadius={0} variant={routeMode === 'assigned' ? 'solid' : 'ghost'}
              colorScheme={routeMode === 'assigned' ? 'blue' : 'gray'}
              onClick={() => { setLiveRoutes({}); setRouteMode('assigned'); }}>Assigned (live)</Button>
          </HStack>
          <Button size="sm" variant="outline" leftIcon={<FaSyncAlt />} onClick={() => { setLiveRoutes({}); fetchActive(); }}>Refresh</Button>
        </HStack>
      </Flex>

      <Flex gap={4} direction={{ base: 'column', md: 'row' }} align="stretch">
        {/* Rep list */}
        <Box w={{ base: 'full', md: '320px' }} flexShrink={0}>
          {loading ? (
            <Spinner />
          ) : (reps.length === 0 && assignable.length === 0 && !allRoute) ? (
            <Box p={6} bg="white" borderRadius="lg" borderWidth="1px" textAlign="center">
              <Text color="gray.500">No active routes and no phlebs with open draws.</Text>
              <Text fontSize="xs" color="gray.400" mt={1}>Phlebs appear here once they have assigned draws or start a route — or pick a date above to see who’s scheduled.</Text>
            </Box>
          ) : (
            <VStack align="stretch" spacing={2}>
              {/* All phlebs — one combined route across everyone's open draws */}
              {(() => {
                const totalOpen = assignable.reduce((s, a) => s + a.count, 0);
                const phlebCount = assignable.length;
                const isSel = selectedId === '__all__';
                if (totalOpen === 0 && !allRoute) return null;
                return (
                  <Box
                    p={3}
                    bg={isSel ? 'purple.50' : 'white'}
                    borderWidth="1px"
                    borderColor={isSel ? 'purple.300' : 'purple.200'}
                    borderRadius="lg"
                    _hover={{ borderColor: 'purple.300' }}
                  >
                    <Box as="button" textAlign="left" w="100%" onClick={() => setSelectedId('__all__')}>
                      <HStack justify="space-between">
                        <Text fontWeight="800" color="purple.700" noOfLines={1}>
                          {allRoute?.scheduledFor ? `Scheduled — ${allRoute.scheduledFor}` : 'All phlebs — combined route'}
                        </Text>
                        {allRoute?.scheduledFor
                          ? <Badge colorScheme="purple" borderRadius="full" fontSize="2xs">{allRoute.route?.stops?.length || 0} stop{(allRoute.route?.stops?.length || 0) === 1 ? '' : 's'}</Badge>
                          : <Badge colorScheme="purple" borderRadius="full" fontSize="2xs">{phlebCount} phleb{phlebCount === 1 ? '' : 's'}</Badge>}
                      </HStack>
                      <Text fontSize="xs" color="gray.500">
                        {allRoute?.scheduledFor
                          ? `Everyone scheduled on ${allRoute.scheduledFor}`
                          : `${totalOpen} open draw${totalOpen === 1 ? '' : 's'} across everyone`}
                        {allRoute?.route?.totalMinutes ? ` · ~${allRoute.route.totalMinutes}m` : ''}
                      </Text>
                    </Box>
                    {isSel && (
                      <Box mt={2}>
                        {!allRoute?.scheduledFor && (
                          <Button size="sm" colorScheme="purple" w="100%"
                            isLoading={buildingAll} loadingText="Building & optimizing"
                            onClick={buildAllRoute}>
                            {allRoute ? '↻ Rebuild combined route' : `⚡ Build combined route (${totalOpen})`}
                          </Button>
                        )}
                        {allRoute && routeDetail(allRoute)}
                      </Box>
                    )}
                  </Box>
                );
              })()}

              {/* Active routes first */}
              {reps.map((r) => {
                const stale = Date.now() - new Date(r.updated_at).getTime() > STALE_MS;
                const stops = Array.isArray(r.route?.stops) ? r.route.stops : [];
                const isSel = r.phlebotomist_id === selectedId;
                return (
                  <Box
                    key={r.phlebotomist_id}
                    p={3}
                    bg={isSel ? 'blue.50' : 'white'}
                    borderWidth="1px"
                    borderColor={isSel ? 'blue.300' : 'gray.200'}
                    borderRadius="lg"
                    _hover={{ borderColor: 'blue.300' }}
                  >
                    <Box as="button" textAlign="left" w="100%" onClick={() => selectRep(r)}>
                      <HStack justify="space-between">
                        <HStack spacing={2} minW={0}>
                          <Text fontWeight="700" noOfLines={1}>{repName(r)}</Text>
                          {Array.isArray(r.phlebotomist_profiles?.service_zips) && r.phlebotomist_profiles.service_zips.length > 0 && (
                            <Badge colorScheme="blue" variant="subtle" borderRadius="full" fontSize="2xs" flexShrink={0}>
                              {r.phlebotomist_profiles.service_zips.length} zips
                            </Badge>
                          )}
                        </HStack>
                        <Badge colorScheme={stale ? 'orange' : 'green'} borderRadius="full" fontSize="2xs">
                          {stale ? 'stale' : 'live'}
                        </Badge>
                      </HStack>
                      <Text fontSize="xs" color="gray.500">
                        {stops.length} stop{stops.length === 1 ? '' : 's'}
                        {r.route?.totalMinutes ? ` · ~${r.route.totalMinutes}m drive` : ''} · GPS {timeAgo(r.updated_at)}
                      </Text>
                    </Box>
                    {isSel && (routeMode === 'assigned'
                      ? (buildingLiveId === r.phlebotomist_id || !liveRoutes[r.phlebotomist_id]
                          ? <HStack mt={3} pt={3} borderTopWidth="1px" borderColor="gray.200" spacing={2}><Spinner size="sm" /><Text fontSize="xs" color="gray.500">Building route from current assignments…</Text></HStack>
                          : routeDetail(liveRoutes[r.phlebotomist_id]))
                      : routeDetail(r))}
                  </Box>
                );
              })}

              {/* Phlebs with open draws but no route yet — click to build one */}
              {assignable
                .filter((a) => !reps.some((r) => r.phlebotomist_id === a.id))
                .map((a) => {
                  const isSel = a.id === selectedId;
                  const prof = phlebById[a.id] || {};
                  const name = prof.full_name || prof.company_name || prof.email || 'Phlebotomist';
                  const zips = Array.isArray(prof.service_zips) ? prof.service_zips : [];
                  return (
                    <Box
                      key={a.id}
                      p={3}
                      bg={isSel ? 'blue.50' : 'white'}
                      borderWidth="1px"
                      borderColor={isSel ? 'blue.300' : 'gray.200'}
                      borderRadius="lg"
                      _hover={{ borderColor: 'blue.300' }}
                    >
                      <Box as="button" textAlign="left" w="100%" onClick={() => setSelectedId(a.id)}>
                        <HStack justify="space-between">
                          <HStack spacing={2} minW={0}>
                            <Text fontWeight="700" noOfLines={1}>{name}</Text>
                            {zips.length > 0 && (
                              <Badge colorScheme="blue" variant="subtle" borderRadius="full" fontSize="2xs" flexShrink={0}>
                                {zips.length} zips
                              </Badge>
                            )}
                          </HStack>
                          <Badge colorScheme="gray" borderRadius="full" fontSize="2xs">no route</Badge>
                        </HStack>
                        <Text fontSize="xs" color="gray.500">{a.count} open draw{a.count === 1 ? '' : 's'} · not started</Text>
                      </Box>
                      {isSel && routeMode === 'assigned' && (
                        buildingLiveId === a.id || !liveRoutes[a.id]
                          ? <HStack mt={3} pt={3} borderTopWidth="1px" borderColor="gray.200" spacing={2}><Spinner size="sm" /><Text fontSize="xs" color="gray.500">Building route from current assignments…</Text></HStack>
                          : routeDetail(liveRoutes[a.id])
                      )}
                      {isSel && routeMode === 'active' && (
                        <Box mt={3} pt={3} borderTopWidth="1px" borderColor="gray.200">
                          <Button size="sm" colorScheme="blue" w="100%"
                            isLoading={buildingForId === a.id} loadingText="Building & optimizing"
                            onClick={() => buildRouteForPhleb(a.id)}>
                            ⚡ Build &amp; publish route ({a.count})
                          </Button>
                          <Text fontSize="2xs" color="gray.500" mt={1} textAlign="center">
                            Optimizes all their open draws and publishes — the phleb just logs in and starts.
                          </Text>
                        </Box>
                      )}
                    </Box>
                  );
                })}
            </VStack>
          )}
        </Box>

        {/* Map */}
        <Box flex={1} borderWidth="1px" borderRadius="lg" overflow="hidden" h="640px" bg="white">
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={NYC_CENTER}
              zoom={11}
              options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}
              onLoad={(m) => { mapRef.current = m; }}
            >
              {reps.filter((r) => r.lat != null && r.lng != null).map((r) => (
                <Marker
                  key={r.phlebotomist_id}
                  position={{ lat: r.lat, lng: r.lng }}
                  onClick={() => selectRep(r)}
                  title={repName(r)}
                  icon={window.google ? {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 11,
                    fillColor: r.phlebotomist_id === selectedId ? '#3182CE' : '#2F855A',
                    fillOpacity: 1,
                    strokeColor: '#ffffff',
                    strokeWeight: 2,
                  } : undefined}
                  label={{ text: repInitials(r), color: '#ffffff', fontSize: '10px', fontWeight: '700' }}
                  zIndex={r.phlebotomist_id === selectedId ? 9999 : 1}
                />
              ))}
              {directions && <DirectionsRenderer directions={directions} options={{ preserveViewport: true, suppressMarkers: true }} />}
              {/* Stop markers for the selected rep, colored by whether the stop
                  falls inside their chosen service ZIPs (green) or outside (red).
                  Lets admins see at a glance who's outside the coverage area. */}
              {selected && (selected.route?.stops || [])
                .filter((s) => !hiddenStops.has(s.id))
                .map((s, i) => {
                  // Prefer the geocoded position from the route legs (covers
                  // address-only stops); fall back to parsing the stored loc.
                  const pos = stopPositions[s.id] || stopLatLng(s);
                  if (!pos) return null;
                  const cov = coverageOf(s);
                  const fill = cov === 'out' ? '#E53E3E' : cov === 'in' ? '#2F855A' : '#A0AEC0';
                  return (
                    <Marker
                      key={s.id || i}
                      position={pos}
                      title={`${i + 1}. ${s.name}${cov === 'out' ? ' (outside zone)' : ''}`}
                      icon={window.google ? {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 10,
                        fillColor: fill,
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 2,
                      } : undefined}
                      label={{ text: String(i + 1), color: '#ffffff', fontSize: '10px', fontWeight: '700' }}
                    />
                  );
                })}
            </GoogleMap>
          ) : (
            <Flex h="100%" align="center" justify="center"><Spinner size="lg" /></Flex>
          )}
        </Box>
      </Flex>
    </Container>
  );
}
